"use client";

import React, { useState, useRef, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, Mail, Phone } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function StartStrategicCollaboration() {
  const [mousePos, setMousePos] = useState({
    x: 0,
    y: 0
  });
  const [isHovering, setIsHovering] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const {
    ref: contentRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };
  return <section data-section-id="120436" id="protocollo-di-sicurezza" ref={sectionRef} onMouseMove={handleMouseMove} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} className="dark bg-[#050505] text-[#E5E5E5] relative overflow-hidden py-24 md:py-32 lg:py-40 border-y border-white/5">
      {}
      <div className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-500 ease-in-out" style={{
      opacity: isHovering ? 0.12 : 0,
      background: `radial-gradient(800px circle at ${mousePos.x}px ${mousePos.y}px, #DC143C, transparent 40%)`
    }} />

      {}
      <div className="pointer-events-none absolute inset-0 z-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-30" />

      <div ref={contentRef} className={`container relative z-10 mx-auto px-4 md:px-6 flex flex-col items-center text-center transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="inline-flex items-center rounded-none border border-[#DC143C]/40 bg-[#DC143C]/5 px-4 py-1.5 text-xs font-mono font-medium text-[#DC143C] mb-10 backdrop-blur-md">
          <span className="flex h-1.5 w-1.5 rounded-none bg-[#DC143C] mr-3 animate-pulse"></span>
          [CAPACITÀ OPERATIVA 2026: 3 PARTNER. SLOT RIMANENTI: 2]
        </div>

        <h2 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight mb-8 max-w-5xl text-balance font-serif uppercase">
          L'Infrastruttura Invisibile per <span className="text-[#DC143C]">Dominare l'Est.</span>
        </h2>

        <p className="text-lg md:text-xl text-gray-400 max-w-3xl mb-12 text-balance font-sans font-normal leading-relaxed">
          Ingegnerizziamo la penetrazione commerciale, blindiamo la logistica e ti sediamo ai tavoli dei grandi distributori in Russia, Kazakistan, Azerbaigian e CSI.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto">
          {}
          <div className="relative group w-full sm:w-auto">
            <div className="absolute -inset-1 rounded-none bg-[#DC143C] opacity-20 blur-sm transition-all duration-500 group-hover:opacity-50 group-hover:blur-md group-hover:duration-200"></div>
            <Button size="lg" className="relative w-full sm:w-auto h-16 px-10 text-base font-bold uppercase tracking-widest bg-[#DC143C] text-white hover:bg-[#DC143C]/90 border border-[#DC143C]/50 rounded-none transition-all duration-300" asChild>
              <Link to="#">
                Richiedi Valutazione di Accesso
                <ArrowRight className="ml-3 size-5 transition-transform duration-300 group-hover:translate-x-2" />
              </Link>
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-8 text-sm text-gray-500 mt-6 sm:mt-0 font-sans tracking-wide">
            <Link to="mailto:ops@eurlogix.com" className="flex items-center hover:text-[#DC143C] transition-colors duration-300">
              <Mail className="mr-2.5 size-4" />
              ops@eurlogix.com
            </Link>
            <span className="hidden sm:inline-block h-5 w-px bg-white/10"></span>
            <Link to="tel:+390288776655" className="flex items-center hover:text-[#DC143C] transition-colors duration-300">
              <Phone className="mr-2.5 size-4" />
              +39 02 8877 6655
            </Link>
          </div>
        </div>
      </div>
    </section>;
}